import {Accordion, AccordionSummary, AppBar, Dialog, IconButton, Link, Toolbar, Typography} from '@material-ui/core';
import {fromResult, isLoaded, toPage, toPageEither} from 'Core/models/Async';
import {
  getApplications,
  changeApplicationsFilters,
  FilterEnum,
  clearApplicationFilters,
  clearApplications,
  changeEnrollmentApplicationErrorDetailFilters,
  enrollmentApplicationErrorDetailFiltersEmpty,
  changeGenelcoApplicationSort,
  clearGenelcoApplicationSort,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import Column from 'Shared/components/PagedView/Column';
import {
  withInequalityFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import CloseIcon from '@material-ui/icons/Close';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import './GenelcoApplicationsModal.sass';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIcon} from 'Shared/components/Toolbar/ToolbarHeader';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import GenelcoSpreadsheetFileErrorDetailsDialog from './GenelcoSpreadsheetFileErrorDetailsDialog';
import {pipe} from 'fp-ts/lib/pipeable';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import * as EnrollmentFileActions from 'Enrollment/ducks/enrollmentFileProcessing';
import { EnrollmentRouteConstants } from 'Enrollment/Routes';
import { useHistory } from 'react-router-dom';

type GenelcoApplicationsModalProps = {
  close: () => void;
  enrollmentFile: EnrollmentApplication;
};

const GenelcoApplicationsModal = (props: GenelcoApplicationsModalProps) => {
  const {close, enrollmentFile} = props;
  const {
    applications,
    applicationsFilters,
    enrollmentFileErrorDetails,
    sort
  } = useSelector((state: ReducerState) => state.enrollmentFileProcessing);


  const {enrollmentFilesFilter} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);
  let enrollmentFilesFilterData = enrollmentFilesFilter as EnrollmentFileActions.ESREnrollmentFileFilters;


  const history = useHistory();

  const handlecloseForEnrollmentFile = () => {
    props.close();

    if (enrollmentFilesFilterData.isEnrollmentFileDetails) {
      history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING);
    }
  };

  const dispatch = useDispatch();
  const changeFilter = withValueChangedHook(applicationsFilters, changeApplicationsFilters, dispatch);
  const enrollmentFileErrorDetailsList =
    enrollmentFileErrorDetails.key !== 'Empty'
      ? pipe(
          toPageEither(enrollmentFileErrorDetails),
          E.fold(
            () => [],
            Data => Data.result
          )
        )!
      : [];
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const handleClose = () => {
    setOpenErrorBox(false);
    dispatch(changeEnrollmentApplicationErrorDetailFilters(enrollmentApplicationErrorDetailFiltersEmpty));
  };
  const handleOpen = () => {
    setOpenErrorBox(true);
    dispatch(changeEnrollmentApplicationErrorDetailFilters(enrollmentApplicationErrorDetailFiltersEmpty));
  };
  const filterProperties = {
    changeFilter,
    filterData: applicationsFilters,
  };

  useEffect(() => {
    dispatch(getApplications({...applicationsFilters, id: enrollmentFile.id!},  O.toUndefined(sort)));
  }, [applicationsFilters, sort]);

  
  useEffect(() => {
    return () => {
      dispatch(clearApplications());
      dispatch(clearApplicationFilters());
      dispatch(clearGenelcoApplicationSort());
    };
  }, []);

  const requestTypeOptions: FilterEnum[] = [
    {name: 'Coverage Election', value: 'Coverage Election'},
    {name: 'Coverage Change', value: 'Coverage Change'},
    {name: 'Termination', value: 'Termination'},
    {name: 'Reinstatement', value: 'Reinstatement'},
    {name: 'Admin Change', value: 'Admin Change'},
    {name: 'Non-Coverage Demographic Change', value: 'Non-Coverage Demographic Change'},
    {name: 'No Changes Detected', value: 'No Changes Detected'},
    {name: 'Undetermined', value: 'Undetermined'},
  ];

  const statusOptions: FilterEnum[] = [
    {name: 'Successful', value: 'Successful'},
    {name: 'In Progress', value: 'In Progress'},
    {name: 'Unsuccessful', value: 'Unsuccessful'},
    {name: 'Cancelled', value: 'Cancelled'},
  ];

  const stepOptions: FilterEnum[] = [
    {name: 'File Uploaded', value: 'File Uploaded'},
    {name: 'Received', value: 'Received'},
    {name: 'Processed', value: 'Processed'},
    {name: 'Confirmed', value: 'Confirmed'},
  ];
  return (
    <Dialog className="genelco-applications" fullScreen open>
      <AppBar className="genelco-applications__dialog-header">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handlecloseForEnrollmentFile} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6">File Summary</Typography>
        </Toolbar>
      </AppBar>
      <ModularHost>
        <ToolbarHeader
          title={
            enrollmentFilesFilterData.isEnrollmentFileDetails
              ? 'Enrollment File Application Details'
              : 'Application Details'
          }
        >
          <ExportToolbarHeaderIcon
            title="Export Applications"
            disabled={!isLoaded(applications) || applications.Data.length === 0}
            downloadedFileName="Application details"
            extension={FileExtension.CSV}
            relativeFileLocation={`/enrollmentFiles/applications/${addFilterParameters({
              ...applicationsFilters,
              id: enrollmentFile.id!,
              exportToCsv: true,
            })}`}
            totalRecordCount={0}
          />
        </ToolbarHeader>
        <Accordion className="file-summary-line-info__expansion-panel genelco-application-summary">
          <AccordionSummary>
            <div className="summary-line-info__expansion-panel__icon-container">
              <i className="icon-container__file-icon"></i>
            </div>
            <div className="summary-line-info__expansion-panel__file-details">
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">File Name:</span>
                <span className="value">
                    {enrollmentFile.fileName!}
                </span>
              </div>
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Uploaded:</span>
                <span className="value">{toFormattedDateTime(enrollmentFile.fileUploadedDate!)}</span>
              </div>
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Application Count:</span>
                <span className="value">{enrollmentFile.rowCount!}</span>
              </div>
            </div>
          </AccordionSummary>
        </Accordion>
        {renderAsyncView(applications, enrollmentApplication => (
          <>
            <PagedView
              pagedViewKey="enrollment-applications"
              data={fromResult(toPage(enrollmentApplication))}
              sort={O.toUndefined(sort)}
              onChangeSort={sort => dispatch(changeGenelcoApplicationSort(sort))}
              loadMore={() => {}}
            >
              {columnProps => (
                <>
                  <Column
                    {...columnProps}
                    title="First Name"
                    sortColumnKey="firstName"
                    filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
                    default
                  >
                    {application => application.firstName}
                  </Column>
                  <Column
                    {...columnProps}
                    title="Last Name"
                    sortColumnKey="lastName"
                    filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
                    default
                  >
                    {application => application.lastName}
                  </Column>
                  <Column
                    {...columnProps}
                    title="SSN"
                    sortColumnKey="ssn"
                    filterProperties={withStringFilterProperties(filterProperties, 'ssn')}
                    default
                  >
                    {application => application.ssn}
                  </Column>
                  <Column
                    {...columnProps}
                    title="Error Count"
                    sortColumnKey="totalErrorCount"
                    filterProperties={withInequalityFilterProperties(filterProperties, 'errorCount')}
                    default
                  >
                    {application =>
                      application.totalErrorCount !== 0 ? (
                        <Link onClick={handleOpen}>{application.totalErrorCount}</Link>
                      ) : (
                        application.totalErrorCount
                      )
                    }
                  </Column>
                  <Column
                    {...columnProps}
                    title="Request Type"
                    sortColumnKey="requestType"
                    filterProperties={withSelectFilterProperties(
                      filterProperties,
                      'requestTypes',
                      requestTypeOptions,
                      true
                    )}
                    default
                  >
                    {application => application.requestType?.name}
                  </Column>
                  <Column
                    {...columnProps}
                    title="Step"
                    sortColumnKey="step"
                    filterProperties={withSelectFilterProperties(filterProperties, 'steps', stepOptions, true)}
                    default
                  >
                    {application => application.step}
                  </Column>
                  <Column
                    {...columnProps}
                    title="Status"
                    sortColumnKey="status"
                    filterProperties={withSelectFilterProperties(filterProperties, 'statuses', statusOptions, true)}
                    default
                  >
                    {application => application.status}
                  </Column>
                </>
              )}
            </PagedView>
            <GenelcoSpreadsheetFileErrorDetailsDialog
              dialogOpen={openErrorBox}
              dialogClose={handleClose}
              employeeErrorDetails={enrollmentFileErrorDetailsList}
              enrollmentFileId={enrollmentFile.id!}
              fileName={enrollmentFile.fileName!}
              applicationCount={enrollmentFile.rowCount!}
              applicationErrorCount={
                enrollmentFile.invalidAuditCount! +
                enrollmentFile.invalidRecordCount! +
                enrollmentFile.groupWebComparisonErrorCount!
              }
              uploadedDate={toFormattedDateTime(enrollmentFile.fileUploadedDate!)}
            />
          </>
        ))}
      </ModularHost>
    </Dialog>
  );
};

export default GenelcoApplicationsModal;
